'use strict';
class CustomSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.slider = this.querySelector('.swiper');
    this.nextBtn = this.querySelector('.swiper-button-next');
    this.prevBtn = this.querySelector('.swiper-button-prev');
    this.pagination = this.querySelector('.swiper-pagination');

    this.handleResize = this.handleResize.bind(this);
  }
  
  connectedCallback() {

    this.initSwiper();
    if (this.nextBtn && this.prevBtn) {
      this.setupExternalNavigation();
    }
    this.setupThemeEditorEvents();
    
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.destroySwiper();
  }

  handleResize() {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  isSlideshow() {
    return this.classList.contains('slideshow-banner');
  }
}

CustomSlider.prototype.initSwiper = function () {
  if (!this.slider) return;

  let { 
    speed, 
    loop, 
    slidesCount, 
    slidesCountTablet, 
    slidesCountTabletLarge,
    slidesCountMobile, 
    spaceBetween, 
    spaceBetweenTablet, 
    spaceBetweenMobile, 
    pagination, 
    paginationStyle,
    autoplay, 
    autoplayDelay, 
    autoplayDisableOnInteraction, 
    direction,
    changeEffect
  } = this.dataset;

  speed = Number(speed) || 800;
  loop = loop === 'true';
  slidesCount = slidesCount === "auto" ? "auto" : Number(slidesCount) || 1;
  slidesCountTablet = slidesCountTablet ?? slidesCount;
  slidesCountTablet = slidesCountTablet === "auto" ? "auto" : Number(slidesCountTablet) || 1;
  slidesCountTabletLarge = slidesCountTabletLarge ?? slidesCountTablet;
  slidesCountTabletLarge = slidesCountTabletLarge === "auto" ? "auto" : Number(slidesCountTabletLarge) || slidesCountTablet;
  slidesCountMobile = slidesCountMobile === "auto" ? "auto" : Number(slidesCountMobile) || 1;
  spaceBetween = Number(spaceBetween) || 20;
  spaceBetweenTablet = Number(spaceBetweenTablet) || spaceBetween || 20;
  spaceBetweenMobile = Number(spaceBetweenMobile) || spaceBetween || 20;
  pagination = pagination === 'true';
  paginationStyle = paginationStyle === 'pill_dots' ? 'pill_dots' : 'long_dots';

  autoplay = autoplay === 'true';
  autoplayDelay = Number(autoplayDelay) || 5000;
  autoplayDisableOnInteraction = autoplayDisableOnInteraction === 'true';

  let swiperDirection = 'horizontal';
  let swiperEffect = 'slide';
  
  if (this.isSlideshow()) {
    switch (direction) {
      case 'vertical':
        swiperDirection = 'vertical';
        swiperEffect = 'slide';
        break;
      case 'horizontal':
        swiperDirection = 'horizontal';
        swiperEffect = 'slide';
        break;
      case 'fade':
        swiperDirection = 'horizontal';
        swiperEffect = 'fade';
        break;
      default:
        swiperDirection = 'horizontal';
        swiperEffect = 'slide';
    }
  } else {
    swiperDirection = direction === 'vertical' ? 'vertical' : 'horizontal';
    swiperEffect = changeEffect === 'fade' ? 'fade' : 'slide';
  }

  const swiperOptions = {
    direction: swiperDirection,
    effect: swiperEffect,
    loop: loop,
    slidesPerView: slidesCountMobile,
    spaceBetween: spaceBetweenMobile, 
    grabCursor: true,
    speed: speed,
    keyboard: {
      enabled: true,
    },
    mousewheel: {
      enabled: false,
    },
    breakpoints: {
      750: {
        slidesPerView: slidesCountTablet,
        spaceBetween: spaceBetweenTablet
      },
      989: {
        slidesPerView: slidesCountTabletLarge,
        spaceBetween: spaceBetweenTablet
      },
      1025: {
        slidesPerView: slidesCount,
        spaceBetween: spaceBetween
      }, 
    },
    pagination: pagination && this.pagination ? 
      {
        el: this.pagination,
        clickable: true,
        renderBullet: (index, className) => {
          return `<button class="slider-counter__link slider-counter__link--${paginationStyle} link ${className}" aria-label="Go to slide ${index + 1}"><span class="dot"></span></button>`;
        },
      }
    : false,
    autoplay: autoplay ? {
        delay: autoplayDelay,
        disableOnInteraction: autoplayDisableOnInteraction,
        pauseOnMouseEnter: true
      }
    : false,
    a11y: {
      enabled: true,
      prevSlideMessage: 'Previous slide',
      nextSlideMessage: 'Next slide',
      firstSlideMessage: 'This is the first slide',
      lastSlideMessage: 'This is the last slide',
      paginationBulletMessage: 'Go to slide {{index}}'
    },
    on: {
      init: () => {
        this.onSliderInit();
      },
      slideChange: () => {
        this.onSlideChange();
      },
      slideChangeTransitionEnd: () => {
        this.onSlideChangeEnd();
      }
    }
  };

  if (swiperEffect === 'fade') {
    swiperOptions.fadeEffect = {
      crossFade: true
    };
  }

  this.swiper = new Swiper(this.slider, swiperOptions);
  
  if (!loop) {
    this.updateNavButtons();
    this.swiper.on('slideChange', () => {
      this.updateNavButtons();
    });
  }

  this.setupAccessibility();
};

CustomSlider.prototype.setupExternalNavigation = function () {
  if (this.nextBtn && this.prevBtn) {
    this.nextBtn.addEventListener('click', () => {
      if (this.swiper) this.swiper.slideNext();
    });
  
    this.prevBtn.addEventListener('click', () => {
      if (this.swiper) this.swiper.slidePrev();
    });
  }
};

CustomSlider.prototype.updateNavButtons = function () {
  if (!this.swiper || !this.nextBtn || !this.prevBtn) return;

  if (this.swiper.isBeginning) {
    this.prevBtn.setAttribute('disabled', 'true');
    this.prevBtn.classList.add('disabled');
  } else {
    this.prevBtn.removeAttribute('disabled');
    this.prevBtn.classList.remove('disabled');
  }

  if (this.swiper.isEnd) {
    this.nextBtn.setAttribute('disabled', 'true');
    this.nextBtn.classList.add('disabled');
  } else {
    this.nextBtn.removeAttribute('disabled');
    this.nextBtn.classList.remove('disabled');
  }
};

CustomSlider.prototype.setupAccessibility = function () {
  this.addEventListener('focusin', () => {
    if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) {
      this.swiper.autoplay.stop();
    }
  });

  this.addEventListener('focusout', () => {
    if (this.swiper && this.swiper.autoplay && this.dataset.autoplay === 'true') {
      setTimeout(() => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.start();
        }
      }, 100);
    }
  });
};

CustomSlider.prototype.setupThemeEditorEvents = function () {
  if (!window.Shopify?.designMode) return;

  document.addEventListener('shopify:block:select', (event) => {
    if (!this.contains(event.target)) return;
    
    const blockElement = event.target.closest('.swiper-slide');
    if (blockElement && this.swiper) {
      const slideIndex = Array.from(this.querySelectorAll('.swiper-slide')).indexOf(blockElement);
      this.swiper.slideTo(slideIndex);
      
      if (this.swiper.autoplay) {
        this.swiper.autoplay.stop();
      }
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    if (!this.contains(event.target)) return;
    
    if (this.swiper && this.swiper.autoplay && this.dataset.autoplay === 'true') {
      this.swiper.autoplay.start();
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    if (!this.contains(event.target)) return;
    
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
  });
};

CustomSlider.prototype.onSliderInit = function () {
  this.updateSlideCounter();
};

CustomSlider.prototype.onSlideChange = function () {
  this.updateSlideCounter();
};

CustomSlider.prototype.onSlideChangeEnd = function () {
  if (this.isSlideshow()) {
    this.triggerParallaxAnimation();
  }
};

CustomSlider.prototype.updateSlideCounter = function () {
  const counterElement = this.querySelector('.slider-counter--current');
  if (counterElement && this.swiper) {
    counterElement.textContent = this.swiper.realIndex + 1;
  }
};

CustomSlider.prototype.triggerParallaxAnimation = function () {
  const activeSlide = this.querySelector('.swiper-slide-active');
  if (activeSlide) {
    const parallaxElement = activeSlide.querySelector('parallax-slideshow');
    if (parallaxElement && typeof parallaxElement.animateImages === 'function') {
      parallaxElement.animateImages();
    }
  }
};

CustomSlider.prototype.destroySwiper = function () {
  if (this.swiper) {
    this.swiper.destroy(true, true);
    this.swiper = null;
  }
};

CustomSlider.prototype.next = function () {
  if (this.swiper) this.swiper.slideNext();
};

CustomSlider.prototype.prev = function () {
  if (this.swiper) this.swiper.slidePrev();
};

CustomSlider.prototype.slideTo = function (index) {
  if (this.swiper) this.swiper.slideTo(index);
};

customElements.define('custom-slider', CustomSlider);


class CustomFeaturesSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.slider = this.querySelector('.swiper');
    this.nextBtn = this.querySelector('.swiper-button-next');
    this.prevBtn = this.querySelector('.swiper-button-prev');
    this.pagination = this.querySelector('.swiper-pagination');
    this.timelapseSelect = this.querySelector('.timelapse-navigation-select');

    this.handleResize = this.handleResize.bind(this);
  }
  
  connectedCallback() {
    this.initSwiper();
    this.setupExternalNavigation();
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    if (this.swiper) {
      this.swiper.update();
    }
  }
}

CustomFeaturesSlider.prototype.initSwiper = function () {
  if (!this.slider) return;

  let { speed, loop, slidesCount, slidesCountMobile, spaceBetween, pagination, autoplay, autoplayDelay, autoplayDisableOnInteraction  } = this.dataset;

  speed = Number(speed) || 800;
  loop = loop === 'true';
  slidesCount = slidesCount === "auto" ? "auto" : Number(slidesCount) || 1;
  slidesCountMobile = slidesCountMobile === "auto" ? "auto" : Number(slidesCountMobile) || 1;
  spaceBetween = Number(spaceBetween) || 20;
  pagination = pagination === 'true' && this.pagination;

  autoplay = autoplay === 'true';
  autoplayDelay = Number(autoplayDelay) || 5000;
  autoplayDisableOnInteraction = autoplayDisableOnInteraction === 'true';


  let slideTitles = [];
  if (pagination) {
    const slides = Array.from(this.slider.querySelectorAll('.swiper-slide .multicolumn-card'));
    const timelapseData = slides.map(slide => slide.dataset.timelapse || '');
    
    const hasTimelapseData = timelapseData.some(data => data.trim() !== '');

    if (hasTimelapseData) {
      slideTitles = slides.map((slide, index) => {
        const timelapseValue = timelapseData[index].trim();
        if (timelapseValue !== '') {
          return timelapseValue;
        } else {
          const titleEl = slide.querySelector('.features-title');
          return titleEl ? titleEl.textContent.trim() : '';
        }
      });
    } else {
      pagination = false;
    }
  }

  const swiperOptions = {
    loop: loop,
    slidesPerView: slidesCountMobile,
    spaceBetween: spaceBetween, 
    grabCursor: true,
    speed: speed,
    keyboard: {
      enabled: true,
    },
    pagination: pagination
    ? {
        el: this.pagination,
        clickable: true,
        renderBullet: (index, className) => {
          const title = slideTitles[index] || '';
          const sizeClass = this.dataset.timelapseNavSize || 'h3';
          return `<button type="button" class="${className} timelapse-item ${sizeClass}">${title}</button>`;
        },
      }
    : false,
    breakpoints: {
      750: {
        slidesPerView: slidesCount,
      },
      1025: {
        slidesPerView: slidesCount,
      }, 
    }
  }

  this.swiper = new Swiper(this.slider, swiperOptions);

  if (this.timelapseSelect && this.swiper && pagination) {
    this.timelapseSelect.addEventListener('change', () => {
      const idx = Number(this.timelapseSelect.value, 10);
      if (!Number.isNaN(idx)) this.swiper.slideTo(idx);
    });
    this.swiper.on('slideChange', () => {
      this.timelapseSelect.selectedIndex = this.swiper.activeIndex;
    });
    this.timelapseSelect.selectedIndex = this.swiper.activeIndex;
  }

  if (!loop) {
    this.updateNavButtons();
    this.swiper.on('slideChange', () => {
      this.updateNavButtons();
    });
  }
};


CustomFeaturesSlider.prototype.setupExternalNavigation = function () {

  if (this.nextBtn && this.prevBtn) {
    this.nextBtn.addEventListener('click', () => {
      this.swiper.slideNext();
    });
  
    this.prevBtn.addEventListener('click', () => {
      this.swiper.slidePrev();
    });
  }
};

CustomFeaturesSlider.prototype.updateNavButtons = function () {
  if (!this.swiper || !this.nextBtn || !this.prevBtn) return;

  if (this.swiper.isBeginning) {
    this.prevBtn.setAttribute('disabled', 'true');
    this.prevBtn.classList.add('disabled');
  } else {
    this.prevBtn.removeAttribute('disabled');
    this.prevBtn.classList.remove('disabled');
  }

  if (this.swiper.isEnd) {
    this.nextBtn.setAttribute('disabled', 'true');
    this.nextBtn.classList.add('disabled');
  } else {
    this.nextBtn.removeAttribute('disabled');
    this.nextBtn.classList.remove('disabled');
  }
};

customElements.define('custom-features-slider', CustomFeaturesSlider);



class ReverseSlideshowSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper1 = null;
    this.swiper2 = null;
    this.swiper3 = null;

    this.slider1 = this.querySelector('.swiper.swiper--fade');
    this.slider2 = this.querySelector('.swiper.slideshow_left');
    this.slider3 = this.querySelector('.swiper.slideshow_right');

    this.pagination = this.querySelector('.slideshow__control-wrapper');
  }
  
  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    if (!this.slider1) return;

    let { speed, pagination, autoplay, autoplayDelay, autoplayDisableOnInteraction } = this.dataset;
    const loop = false;

    speed = Number(speed) || 800;
    pagination = pagination === 'true';
    autoplay = autoplay === 'true';
    autoplayDelay = Number(autoplayDelay) || 10000;
    autoplayDisableOnInteraction = autoplayDisableOnInteraction === 'true';

    const swiperOptions1 = {
      speed: speed,
      autoplay: autoplay ? {
        delay: autoplayDelay,
        disableOnInteraction: autoplayDisableOnInteraction,
        pauseOnMouseEnter: true
      } : false,
      slidesPerView: 1,
      spaceBetween: 0, 
      loop: loop,
      grabCursor: false,
      effect: "fade",
      fadeEffect: {
        crossFade: true,
      },
      keyboard: {
        enabled: true,
      },
      pagination: pagination && this.pagination ? 
        {
          el: this.pagination,
          clickable: true,
          renderBullet: function (index, className) {
            return '<button class="slider-counter__link slider-counter__link--long_dots link ' + className + '"><span class="dot"></span></button>';
          },
        }
      : false,
    };

    const swiperOptions2 = {
      speed: speed,
      autoplay: autoplay ? {
        delay: autoplayDelay,
        disableOnInteraction: autoplayDisableOnInteraction,
        pauseOnMouseEnter: true
      }
    : false,
      loop: loop,
      slidesPerView: 1,
      spaceBetween: 0,
      direction: 'vertical',
    };

    const swiperOptions3 = {
      speed: speed,
      autoplay: autoplay ? {
        delay: autoplayDelay,
        disableOnInteraction: autoplayDisableOnInteraction,
        pauseOnMouseEnter: true
      } : false,
      loop: loop,
      slidesPerView: 1,
      spaceBetween: 0,
      direction: 'vertical',
    };

    this.swiper1 = new Swiper(this.slider1, swiperOptions1);
    this.swiper2 = new Swiper(this.slider2, swiperOptions2);
    this.swiper3 = new Swiper(this.slider3, swiperOptions3);

    this.swiper1.on("slideChange", () => {
      const index = this.swiper1.realIndex;
      this.swiper2.slideToLoop(index);
      this.swiper3.slideToLoop(index);
    });
  }
}

customElements.define('reverse-slider-component', ReverseSlideshowSlider);



class CollectionListSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.nextBtn = this.querySelector('.swiper-button-next');
    this.prevBtn = this.querySelector('.swiper-button-prev');
    this.slider = this.querySelector('.swiper');    
  }
  
  connectedCallback() {
    this.initSwiper();
    this.setupExternalNavigation();
  }
}

CollectionListSlider.prototype.initSwiper = function () {
  if (!this.slider) return;

  const { speed, changeSpeed, rotate, slidesCount, horizontalSpace} = this.slider.dataset;

  const swiperOptions = {
    loop: true,
    slidesPerView: 'auto',
    spaceBetween: horizontalSpace, 
    grabCursor: true, 
    keyboard: {
      enabled: true,
    },
    speed,
  }

  if (rotate == 'true') {
    swiperOptions.autoplay = {
      delay: changeSpeed
    };
  }

  this.swiper = new Swiper(this.slider, swiperOptions);
};

CollectionListSlider.prototype.setupExternalNavigation = function () {
  if (!this.nextBtn ||!this.prevBtn) return;

  this.nextBtn.addEventListener('click', () => {
    this.swiper.slideNext();
  });
  this.prevBtn.addEventListener('click', () => {
    this.swiper.slidePrev();
  });
};

customElements.define('collection-list-slider', CollectionListSlider);



class ArticleSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.nextBtn = this.querySelector('.swiper-button-next');
    this.prevBtn = this.querySelector('.swiper-button-prev');
    this.slider = this.querySelector('.swiper');    
  }
  
  connectedCallback() {
    this.initSwiper();
    this.setupExternalNavigation();
  }
}

ArticleSlider.prototype.initSwiper = function () {
  if (!this.slider) return;

  const swiperOptions = {
    loop: true,
    slidesPerView: 1,
    autoHeight: true,
    spaceBetween: 10, 
    grabCursor: true, 
    keyboard: {
      enabled: true,
    },
  }

  this.swiper = new Swiper(this.slider, swiperOptions);
};

ArticleSlider.prototype.setupExternalNavigation = function () {
  if (!this.nextBtn || !this.prevBtn) return;

  this.nextBtn.addEventListener('click', () => {
    this.swiper.slideNext();
  });
  this.prevBtn.addEventListener('click', () => {
    this.swiper.slidePrev();
  });
};

customElements.define('article-slider', ArticleSlider);



class BlogPostsSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.nextBtn = this.querySelector('.swiper-button-next');
    this.prevBtn = this.querySelector('.swiper-button-prev');
    this.slider = this.querySelector('.swiper');    
  }
  
  connectedCallback() {
    this.initSwiper();
    this.setupExternalNavigation();

    this.updateNavButtons();
    this.swiper.on('slideChange', () => {
      this.updateNavButtons();
    });
    this.swiper.on('breakpoint', () => {
      this.updateNavButtons();
    });
    this.swiper.on('resize', () => {
      this.updateNavButtons();
    });
  }
}

BlogPostsSlider.prototype.initSwiper = function () {
  if (!this.slider) return;

  const { speed, changeSpeed, spaceBetween, rotate} = this.dataset;
  const swiperOptions = {
    loop: false,
    slidesPerView: 1,
    spaceBetween: 20, 
    grabCursor: true, 
    autoHeight: true,
    keyboard: {
      enabled: true,
    }, 		
    speed,
    breakpoints: {
      750: {
        autoHeight: false,
        spaceBetween: spaceBetween,
        slidesPerView: 1,
      },
      1025: {
        autoHeight: false,
        spaceBetween: spaceBetween,
        slidesPerView: 1.65,
      },
      1200: {
        autoHeight: false,
        spaceBetween: spaceBetween,
        slidesPerView: 3,
      },
    }
  }

  if (rotate == 'true') {
    swiperOptions.autoplay = {
      delay: changeSpeed
    };
  }

  this.swiper = new Swiper(this.slider, swiperOptions);
};

BlogPostsSlider.prototype.setupExternalNavigation = function () {
  if (!this.nextBtn ||!this.prevBtn) return;

  this.nextBtn.addEventListener('click', () => {
    this.swiper.slideNext();
  });
  this.prevBtn.addEventListener('click', () => {
    this.swiper.slidePrev();
  });
};

BlogPostsSlider.prototype.updateNavButtons = function () {
  if (!this.swiper || !this.nextBtn || !this.prevBtn) return;

  const epsilon = 1;
  const minTranslate = this.swiper.minTranslate();
  const maxTranslate = this.swiper.maxTranslate();
  const currentTranslate = this.swiper.translate;
  const progress = Number.isFinite(this.swiper.progress) ? this.swiper.progress : 0;

  const isBeginning =
    this.swiper.isBeginning ||
    Math.abs(currentTranslate - minTranslate) <= epsilon ||
    progress <= 0.001;
  const isEnd =
    this.swiper.isEnd ||
    Math.abs(currentTranslate - maxTranslate) <= epsilon ||
    progress >= 0.999;

  if (isBeginning) {
    this.prevBtn.setAttribute('disabled', 'true');
    this.prevBtn.classList.add('disabled');
  } else {
    this.prevBtn.removeAttribute('disabled');
    this.prevBtn.classList.remove('disabled');
  }

  if (isEnd) {
    this.nextBtn.setAttribute('disabled', 'true');
    this.nextBtn.classList.add('disabled');
  } else {
    this.nextBtn.removeAttribute('disabled');
    this.nextBtn.classList.remove('disabled');
  }
};

customElements.define('blog-posts-slider', BlogPostsSlider);



class SlideShowArticles extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.nextBtn = this.querySelector('.swiper-button-next');
    this.prevBtn = this.querySelector('.swiper-button-prev');
    this.slider = this.querySelector('.swiper');    
  }
  
  connectedCallback() {
    this.initSwiper();

    if (this.nextBtn && this.prevBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.swiper.slideNext();
      });
      this.prevBtn.addEventListener('click', () => {
        this.swiper.slidePrev();
      });
    }
  }
}

SlideShowArticles.prototype.initSwiper = function () {
  if (!this.slider) return;

  let { speed, changeSpeed, spaceBetween, slidesCount, rotate} = this.dataset;

  slidesCount = slidesCount === "auto" ? "auto" : Number(slidesCount) || 1.4;
  spaceBetween = Number(spaceBetween) || 20;
  const swiperOptions = {
    loop: true,
    slidesPerView: 1.4,
    centeredSlides: true,
    spaceBetween: 20, 
    grabCursor: true,
    speed,
    keyboard: {
      enabled: true,
    }, 		
    breakpoints: {
      750: {
        slidesPerView: slidesCount,
        spaceBetween: spaceBetween, 
      }, 
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
      renderBullet: function (index, className) {
      return `<span class="${className}"><span class="dot"></span></span>`;
      },
    }
  }

  if (rotate == 'true') {
    swiperOptions.autoplay = {
      delay: changeSpeed
    };
  }

  this.swiper = new Swiper(this.slider, swiperOptions);

  this.updateSlideFocus();

  this.swiper.on('slideChange', () => {
    this.updateSlideFocus();
  });
};

SlideShowArticles.prototype.updateSlideFocus = function () {
  if (!this.swiper) return;

  this.swiper.slides.forEach((slide, index) => {
    const focusableEls = slide.querySelectorAll('a, button, input, textarea, select, [tabindex]');
    const mainFocusableEl = slide.querySelector('a.article-card__url');

    focusableEls.forEach(el => {
      el.tabIndex = -1;
    });

    if (index === this.swiper.activeIndex && !slide.classList.contains('swiper-slide-duplicate')) {
      mainFocusableEl.tabIndex = 0;
    }
  });
};


customElements.define('slideshow-articles', SlideShowArticles);



class PreviewSlider extends HTMLElement {
  constructor() {
    super();
    this.mainSwiper = null;
    this.previewSwiper = null;
    this.sliderMain = this.querySelector('.main-swiper');
    this.sliderPreview = this.querySelector('.preview-swiper');
  }

  connectedCallback() {
    this.options = {
      speed: parseInt(this.dataset.speed) || 600,
      changeSpeed: parseInt(this.dataset.changeSpeed) || 3000,
      rotate: this.dataset.rotate === 'true'
    };

    this.initPreviewSwiper();
    this.initMainSwiper();
    this.setupExternalNavigation();
  }

  initMainSwiper() {
    if (!this.sliderMain) return;

    const { speed, changeSpeed, rotate } = this.options;

    const swiperOptions = {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 40,
      grabCursor: true,
      autoHeight: true,
      keyboard: {
        enabled: true,
      },
      speed,
      on: {
        init: () => this.updatePreview(),
        slideChange: () => this.updatePreview()
      }
    };

    if (rotate) {
      swiperOptions.autoplay = {
        delay: changeSpeed,
      };
    }

    this.mainSwiper = new Swiper(this.sliderMain, swiperOptions);

  }

  setupExternalNavigation() {
    this.nextBtn = this.querySelectorAll('.swiper-button-next');
    
    if (this.nextBtn.length === 0) return;
    this.nextBtn.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.mainSwiper.slideNext();
      });
    })
  };

  initPreviewSwiper() {
    if (!this.sliderPreview) return;
    const { speed, changeSpeed, rotate } = this.options;

    this.previewSwiper = new Swiper(this.sliderPreview, {
      loop: true,
      slidesPerView: 1,
      initialSlide: 1,
      allowTouchMove: false,
      effect: "fade",
      speed,
    });

    this.previewSwiper.update();
  }

  updatePreview() {
    if (!this.mainSwiper || !this.previewSwiper) return;

    const realIndex = this.mainSwiper.realIndex;
    const total = this.sliderMain.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;
    const nextIndex = (realIndex + 1) % total;

    this.previewSwiper.slideToLoop(nextIndex);
  }
}

customElements.define('preview-slider', PreviewSlider);




if (!customElements.get('media-gallery-slider')) {
  customElements.define(
    'media-gallery-slider',
    class MediaGallerySlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.swiperThumbnails = null;
        this.swiperMain = null;

        this.sliderMain = this.querySelector('.swiper.slider--main');
        this.sliderThumbnails = this.querySelector('.swiper.slider--thumbnails');
        this.viewToggleCheckbox = this.querySelector('.gallery-slider-button input[type="checkbox"]');
        
        this.updateDefaultSlidesPerView();

        this.initSwiper();
        this.initEvents();
      }

      updateDefaultSlidesPerView() {
        const hasCheckedInput = this.sliderThumbnails?.querySelector('input:checked');
        
        if (hasCheckedInput) {
          this.defaultSlidesPerView = 1;
        } else {
          this.defaultSlidesPerView = 
            this.sliderThumbnails?.classList.contains('slider--thumbnails-2-slides') ? 2 : 3;
        }
      }

      initSwiper() {
        if (!this.sliderMain) return;

        if (this.sliderThumbnails) {
          const swiperOptionsThumbnails = {
            spaceBetween: 0,
            slidesPerView: this.defaultSlidesPerView,
            watchSlidesProgress: true,
            grabCursor: true,
            touchRatio: 1,
          };
          this.swiperThumbnails = new Swiper(this.sliderThumbnails, swiperOptionsThumbnails);
        }

        const swiperOptionsMain = {
          spaceBetween: 5,
          autoHeight: true,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          ...(this.swiperThumbnails && {
            thumbs: {
              swiper: this.swiperThumbnails,
              slideThumbActiveClass: 'swiper-slide-thumb-active',
              thumbsContainerClass: 'swiper-thumbs'
            },
          }),
          on: {
            slideChange: () => {
              this.pauseVideos();
            }
          }
        };
        this.swiperMain = new Swiper(this.sliderMain, swiperOptionsMain);
        
        if (this.swiperThumbnails) {
          setTimeout(() => {
            try {
              this.swiperThumbnails.update();
              if (this.swiperMain.thumbs && this.swiperMain.thumbs.swiper !== this.swiperThumbnails) {
                this.swiperMain.thumbs.init();
                this.swiperMain.thumbs.update();
              }
            } catch (error) {
              console.warn('Post-initialization thumbnails sync failed:', error);
            }
          }, 100);
        }
      }

      initEvents() {
        if (!this.sliderThumbnails || !this.swiperThumbnails) return;

        const thumbnailsInputs = this.sliderThumbnails.querySelectorAll('input');
        
        thumbnailsInputs.forEach(input => {
          input.addEventListener('change', () => {
            this.handleThumbnailsInputChange();
          });
        });

        this.sliderThumbnails.addEventListener('wheel', (event) => {
            if (!this.swiperThumbnails) return;
      
            const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX)
              ? event.deltaY
              : event.deltaX;
      
            if (!delta) return;
      
            event.preventDefault();
      
            if (delta > 0) {
              this.swiperThumbnails.slideNext();
            } else {
              this.swiperThumbnails.slidePrev();
            }
          },
          { passive: false }
        );

        if (this.viewToggleCheckbox) {
          this.viewToggleCheckbox.addEventListener('change', () => {
            this.handleThumbnailsInputChange();
          });
        }

        this.setupInputObserver();
      }

      setupInputObserver() {
        if (!this.sliderThumbnails) return;

        this.inputObserver = new MutationObserver((mutations) => {
          let shouldUpdate = false;
          
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  const newInputs = node.querySelectorAll ? node.querySelectorAll('input') : [];
                  if (newInputs.length > 0 || node.tagName === 'INPUT') {
                    shouldUpdate = true;
                    (newInputs.length > 0 ? newInputs : [node]).forEach(input => {
                      if (input.tagName === 'INPUT') {
                        input.addEventListener('change', () => {
                          this.handleThumbnailsInputChange();
                        });
                      }
                    });
                  }
                }
              });
            }
            
            if (mutation.type === 'attributes' && mutation.target.tagName === 'INPUT' && 
                (mutation.attributeName === 'checked' || mutation.attributeName === 'value')) {
              shouldUpdate = true;
            }
          });
          
          if (shouldUpdate) {
            this.handleThumbnailsInputChange();
          }
        });

        this.inputObserver.observe(this.sliderThumbnails, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['checked', 'value']
        });
      }

      disconnectedCallback() {
        if (this.inputObserver) {
          this.inputObserver.disconnect();
        }
      }

      handleThumbnailsInputChange() {
        this.updateDefaultSlidesPerView();
        
        if (this.swiperThumbnails) {
          this.swiperThumbnails.params.slidesPerView = this.defaultSlidesPerView;
          this.swiperThumbnails.update();
        }
      }

      updateSwiperInstances() {
        try {
          this.updateDefaultSlidesPerView();
          
          if (this.swiperMain && typeof this.swiperMain.update === 'function') {
            this.swiperMain.update();
          }
          if (this.swiperThumbnails && typeof this.swiperThumbnails.update === 'function') {
            this.swiperThumbnails.params.slidesPerView = this.defaultSlidesPerView;
            this.swiperThumbnails.update();
          }
        } catch (error) {
          console.warn('Failed to update swiper instances:', error);
        }
      }

      reinitializeSwiperInstances() {
        
        if (this.swiperMain) {
          this.swiperMain.destroy(true, true);
          this.swiperMain = null;
        }
        if (this.swiperThumbnails) {
          this.swiperThumbnails.destroy(true, true);
          this.swiperThumbnails = null;
        }
        
        setTimeout(() => {
          this.updateDefaultSlidesPerView();
          
          this.initSwiper();
          
        }, 50);
      }

      setActiveMedia(mediaId) {
        if (!this.sliderMain || !this.swiperMain) return;

        window.pauseAllMedia();

        const mainSlides = this.sliderMain.querySelectorAll('.swiper-slide[data-media-id]');
        if (!mainSlides.length) return;
        
        let targetIndex = -1;
        mainSlides.forEach((slide, index) => {
          if (slide.dataset.mediaId === mediaId) {
            targetIndex = index;
          }
        });

        if (targetIndex >= 0 && 
            this.swiperMain && 
            this.swiperMain.slides && 
            targetIndex < this.swiperMain.slides.length) {
          try {
            const currentIndex = this.swiperMain.activeIndex;
            
            this.swiperMain.slideTo(targetIndex, 300, false);
            

            
            if (this.swiperThumbnails && this.swiperMain.thumbs) {
              setTimeout(() => {
                try {
                  this.updateDefaultSlidesPerView();
                  this.swiperThumbnails.params.slidesPerView = this.defaultSlidesPerView;
                  
                  this.swiperMain.thumbs.update();
                  this.swiperThumbnails.update();
                  

                } catch (error) {
                  console.warn('Thumbs update failed:', error);
                }
              }, 350);
            }
          } catch (error) {
            console.warn('Failed to slide to media:', error);
          }
        }
      }

      pauseVideos() {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }
      
        if (!this.swiperMain || !this.swiperMain.slides || !this.swiperMain.slides.length) return;
      
        Array.from(this.swiperMain.slides).forEach((slide) => {
          if (slide.classList.contains('swiper-slide-active')) return;
      
          const mediaElements = slide.querySelectorAll('video, iframe');
      
          mediaElements.forEach((el) => {
            if (el.tagName === 'VIDEO') {
              if (!el.paused) el.pause();
              return;
            }
      
            if (el.tagName === 'IFRAME') {
              try {
                const src = el.src || '';
      
                if (
                  src.includes('youtube.com') ||
                  src.includes('youtu.be') ||
                  src.includes('youtube-nocookie.com')
                ) {
                  if (el.contentWindow) {
                    const message = JSON.stringify({
                      event: 'command',
                      func: 'pauseVideo',
                      args: ''
                    });
                    el.contentWindow.postMessage(message, '*');
                  }
                }
      
                if (src.includes('vimeo.com')) {
                  if (el.contentWindow) {
                    el.contentWindow.postMessage('{"method":"pause"}', '*');
                  }
                }
              } catch (e) {
              }
            }
          });
      
          const modelViewers = slide.querySelectorAll('model-viewer, product-model');
          modelViewers.forEach((model) => {
            if (model.tagName === 'PRODUCT-MODEL' && model.modelViewerUI) {
              model.modelViewerUI.pause();
            } else if (model.tagName === 'MODEL-VIEWER' && model.pause) {
              model.pause();
            }
          });
        });
      }
      
    }
  );
}


if (!customElements.get('subcollections-slider')) {

  class SubcollectionsSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.slider = this.querySelector('.swiper');
      this.nextBtn = this.querySelector('.swiper-button-next');
      this.prevBtn = this.querySelector('.swiper-button-prev');
    }
    
    connectedCallback() {
      this.initSwiper();
      this.setupExternalNavigation();
      this.updateNavButtons();
      this.swiper.on('slideChange', () => {
        this.updateNavButtons();
      });
    }
  }

  SubcollectionsSlider.prototype.initSwiper = function () {
    if (!this.slider) return;

    let { speed, loop, slidesCount, spaceBetween } = this.dataset;

    loop = loop === 'true';
    
    const swiperOptions = {
      loop: loop,
      slidesPerView: 1.2,
      spaceBetween: spaceBetween, 
      grabCursor: true,
      speed: speed,
      keyboard: {
        enabled: true,
      },
      breakpoints: {
        750: {
          slidesPerView: 2.5,
        },
        1025: {
          slidesPerView: slidesCount
        }, 
      }
    }

    this.swiper = new Swiper(this.slider, swiperOptions);
  };

  SubcollectionsSlider.prototype.setupExternalNavigation = function () {
    if (!this.nextBtn ||!this.prevBtn) return;

    this.nextBtn.addEventListener('click', () => {
      this.swiper.slideNext();
    });
    this.prevBtn.addEventListener('click', () => {
      this.swiper.slidePrev();
    });
  };

  SubcollectionsSlider.prototype.updateNavButtons = function () {
    if (!this.swiper || !this.nextBtn || !this.prevBtn) return;
  
    if (this.swiper.isBeginning) {
      this.prevBtn.setAttribute('disabled', 'true');
      this.prevBtn.classList.add('disabled');
    } else {
      this.prevBtn.removeAttribute('disabled');
      this.prevBtn.classList.remove('disabled');
    }
  
    if (this.swiper.isEnd) {
      this.nextBtn.setAttribute('disabled', 'true');
      this.nextBtn.classList.add('disabled');
    } else {
      this.nextBtn.removeAttribute('disabled');
      this.nextBtn.classList.remove('disabled');
    }
  };

  customElements.define('subcollections-slider', SubcollectionsSlider);
}
